import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Users, Mail, Phone, Building, MessageSquare, Calendar,
  LogOut, RefreshCw, CheckCircle, Clock, X, DollarSign
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

// Use relative URL so it works from any domain (360technologies.com or preview URL)
const API = '/api';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, resolved: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [inquiriesRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/inquiries`, { headers }),
        axios.get(`${API}/admin/stats`, { headers })
      ]);

      setInquiries(inquiriesRes.data);
      setStats(statsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      } else {
        toast.error('Error loading data');
      }
      setLoading(false);
    }
  };

  const updateStatus = async (inquiryId, status) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.patch(
        `${API}/admin/inquiries/${inquiryId}/status?new_status=${status}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Status updated successfully');
      fetchData();
    } catch (error) {
      toast.error('Error updating status');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
    toast.success('Logged out successfully');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-100 text-blue-800',
      contacted: 'bg-yellow-100 text-yellow-800',
      resolved: 'bg-green-100 text-green-800'
    };
    return styles[status] || styles.new;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white py-6 px-4 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Tech360 Admin Dashboard</h1>
            <p className="text-gray-400 mt-1">Manage customer inquiries</p>
          </div>
          <div className="flex gap-4">
            <Button
              onClick={() => navigate('/isp/users')}
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10"
            >
              <Users className="w-4 h-4 mr-2" />
              User Management
            </Button>
            <Button
              onClick={() => navigate('/admin/pricing')}
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10"
            >
              <DollarSign className="w-4 h-4 mr-2" />
              Manage Pricing
            </Button>
            <Button
              onClick={fetchData}
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Inquiries</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
                </div>
                <Users className="w-10 h-10 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">New</p>
                  <p className="text-3xl font-bold text-blue-600">{stats.new}</p>
                </div>
                <Clock className="w-10 h-10 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Contacted</p>
                  <p className="text-3xl font-bold text-yellow-600">{stats.contacted}</p>
                </div>
                <Phone className="w-10 h-10 text-yellow-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Resolved</p>
                  <p className="text-3xl font-bold text-green-600">{stats.resolved}</p>
                </div>
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Inquiries List */}
        <Card>
          <CardHeader>
            <CardTitle>Customer Inquiries</CardTitle>
          </CardHeader>
          <CardContent>
            {inquiries.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No inquiries yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {inquiries.map((inquiry) => (
                  <Card key={inquiry.id} className="border-2">
                    <CardContent className="pt-6">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-xl font-bold text-gray-900">{inquiry.name}</h3>
                            <Badge className={getStatusBadge(inquiry.status)}>
                              {inquiry.status}
                            </Badge>
                          </div>
                          
                          <div className="grid md:grid-cols-2 gap-4 mb-4">
                            <div className="flex items-center gap-2 text-gray-600">
                              <Mail className="w-4 h-4" />
                              <a href={`mailto:${inquiry.email}`} className="hover:text-emerald-600">
                                {inquiry.email}
                              </a>
                            </div>

                            {inquiry.phone && (
                              <div className="flex items-center gap-2 text-gray-600">
                                <Phone className="w-4 h-4" />
                                <a href={`tel:${inquiry.phone}`} className="hover:text-emerald-600">
                                  {inquiry.phone}
                                </a>
                              </div>
                            )}

                            {inquiry.company && (
                              <div className="flex items-center gap-2 text-gray-600">
                                <Building className="w-4 h-4" />
                                {inquiry.company}
                              </div>
                            )}

                            <div className="flex items-center gap-2 text-gray-600">
                              <Calendar className="w-4 h-4" />
                              {formatDate(inquiry.created_at)}
                            </div>
                          </div>

                          <div className="mb-4">
                            <p className="text-sm font-semibold text-gray-700 mb-1">Service: {inquiry.service}</p>
                            <p className="text-gray-600 bg-gray-50 p-3 rounded-lg">{inquiry.message}</p>
                          </div>

                          <div className="flex gap-2">
                            {inquiry.status !== 'contacted' && (
                              <Button
                                size="sm"
                                onClick={() => updateStatus(inquiry.id, 'contacted')}
                                className="bg-yellow-600 hover:bg-yellow-700"
                              >
                                Mark as Contacted
                              </Button>
                            )}
                            {inquiry.status !== 'resolved' && (
                              <Button
                                size="sm"
                                onClick={() => updateStatus(inquiry.id, 'resolved')}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                Mark as Resolved
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
